import { Box, Button, Flex, Text } from "@chakra-ui/react";
import { useReq } from "@larner.dev/use-req";
import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { apiReq } from "../../lib/apiReq";
import { store, useStoreData } from "../../lib/store/store";
import { ActionType } from "../../lib/store/storeActions";
import { Icon } from "../Icon/Icon";
import { hasUnfinishedMatching } from "./hasPostEventMatchNudge";

/**
 * Full-tab interstitial shown on the Spark tab funneling users who confuse
 * pre-event Sparking with post-event matching. Users were swiping here and
 * believing they'd completed matching for an event they attended.
 *
 * It blocks the swipe deck (the caller renders the deck only when this returns
 * null) and forces an explicit choice for the OLDEST event with unfinished
 * matching:
 *   - "Yes" navigates to the real post-event flow (/event/:sparkId/match).
 *   - "No, I'm done" persists matches_completed=true via
 *     POST /spark/:sparkId/matches-completed, so this event stops prompting.
 *
 * Both actions clear the current event from the unfinished set (Yes by the user
 * completing matching there, No by marking it complete), so the next-oldest
 * unfinished event surfaces next — one at a time — until none remain, at which
 * point this renders null and the Spark tab is usable again.
 */
export function PostEventMatchNudge() {
  const storeData = useStoreData();
  const navigate = useNavigate();
  const [matchesCompletedReq] = useReq<{ success: true }>(apiReq);
  // Guards the "No" button while the POST is in flight so a double-tap doesn't
  // fire two requests.
  const [saving, setSaving] = useState(false);

  // Pick the OLDEST event with unfinished matching — the one most overdue.
  // pastSparks arrives newest-first (ends_at desc) but we sort explicitly by
  // starts_at rather than trust array order. When the user marks one complete
  // the store update drops it from this set and the next-oldest takes its place.
  const friendsOnly = storeData.user?.friends_only ?? false;
  const targetSpark = useMemo(() => {
    const unfinished = (storeData.pastSparks ?? []).filter((spark) =>
      hasUnfinishedMatching(spark, friendsOnly)
    );
    if (!unfinished.length) return null;
    return unfinished.reduce((oldest, s) =>
      s.starts_at < oldest.starts_at ? s : oldest
    );
  }, [storeData.pastSparks, friendsOnly]);

  if (!targetSpark) return null;

  const eventLabel = targetSpark.title || targetSpark.activity;

  async function markDoneMatching() {
    if (!targetSpark || saving) return;
    setSaving(true);
    const response = await matchesCompletedReq.post(
      `/spark/${targetSpark.id}/matches-completed`,
      {}
    );
    if (response.success) {
      // Flips matchesCompleted=true in the store, which recomputes targetSpark
      // to the next unfinished event (or null → deck reappears).
      store.dispatch({
        type: ActionType.UpdateMatchesCompleted,
        params: { sparkId: targetSpark.id },
      });
    }
    setSaving(false);
  }

  return (
    <Flex
      direction="column"
      align="center"
      justify="center"
      textAlign="center"
      minH="calc(100vh - 12rem)"
      px="1.5rem"
      gap="1.5rem"
    >
      <Flex
        align="center"
        justify="center"
        boxSize="3.5rem"
        borderRadius="full"
        bg="bg.brand"
        color="fg.onBrand"
        flexShrink={0}
      >
        <Icon name="heart-filled" boxSize="1.75rem" />
      </Flex>

      <Box maxW="26rem">
        <Text textStyle="headingBold" mb="0.5rem">
          Want to match from your past event?
        </Text>
        <Text color="fg.muted">
          This isn't your post-event matching. Head to{" "}
          <Text as="span" fontWeight="semibold" color="fg">
            {eventLabel}
          </Text>{" "}
          to choose who you'd like to match with from the people you met there.
        </Text>
      </Box>

      <Flex direction="column" gap="0.75rem" w="100%" maxW="22rem">
        <Button
          size="lg"
          w="100%"
          bg="bg.brand"
          color="fg.onBrand"
          onClick={() => navigate(`/event/${targetSpark.id}/match`)}
        >
          Yes, take me to matching
        </Button>
        <Button
          size="lg"
          w="100%"
          variant="outline"
          colorPalette="gray"
          loading={saving}
          onClick={() => {
            void markDoneMatching();
          }}
        >
          No, I'm done matching
        </Button>
      </Flex>
    </Flex>
  );
}
